import { useRef, type KeyboardEvent } from 'react';
import { DemoBadge } from '../../components/ui.tsx';
import { DEFAULT_RULE, PERSPECTIVES, perspectiveLabel, type PerspectiveId } from './perspectives.ts';

export const tabId = (id: PerspectiveId) => `perspectiva-aba-${id}`;
export const panelId = 'perspectiva-painel';

/**
 * Tabs of the panel perspectives (WAI-ARIA tabs pattern, automatic activation): one tab stop, arrows move between
 * the tabs, Home and End go to the ends. Shown only when the profile has more than one perspective.
 */
export function PerspectiveTabs({
  allowed,
  current,
  defaultId,
  onSelect,
}: {
  allowed: readonly PerspectiveId[];
  current: PerspectiveId;
  defaultId: PerspectiveId | null;
  onSelect: (id: PerspectiveId) => void;
}) {
  const refs = useRef(new Map<PerspectiveId, HTMLButtonElement>());
  const tabs = PERSPECTIVES.filter((p) => allowed.includes(p.id));
  const purpose = PERSPECTIVES.find((p) => p.id === current)?.purpose;

  const move = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = tabs.length - 1;
    const next =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? index === last
          ? 0
          : index + 1
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
          ? index === 0
            ? last
            : index - 1
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? last
              : null;
    if (next === null) return;
    e.preventDefault();
    const target = tabs[next]!.id;
    onSelect(target);
    refs.current.get(target)?.focus();
  };

  return (
    <div data-testid="dashboard-perspectives" data-tour="painel-perspectivas">
      {tabs.length > 1 ? (
        <div className="tabs" role="tablist" aria-label="Perspectiva do painel">
          {tabs.map((p, i) => (
            <button
              key={p.id}
              ref={(el) => {
                if (el) refs.current.set(p.id, el);
                else refs.current.delete(p.id);
              }}
              type="button"
              role="tab"
              id={tabId(p.id)}
              aria-selected={p.id === current}
              aria-controls={panelId}
              tabIndex={p.id === current ? 0 : -1}
              className={p.id === current ? 'active' : ''}
              onClick={() => onSelect(p.id)}
              onKeyDown={(e) => move(e, i)}
            >
              {p.label}
            </button>
          ))}
        </div>
      ) : null}
      <p className="small muted" data-testid="perspective-purpose">
        {purpose}{' '}
        {defaultId ? (
          <>
            Perspectiva inicial do seu perfil: {perspectiveLabel(defaultId)}.{' '}
            <DemoBadge
              title={`Perspectiva inicial por perfil provisória: aguarda a decisão da gestão (${DEFAULT_RULE.decision}).`}
            />
          </>
        ) : null}
      </p>
    </div>
  );
}
